import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  selectAllFolders,
  selectAllProjects,
  selectAllReports,
  selectCurrentAccount,
  selectFolderById,
  selectUserId,
  useGetFolderQuery,
} from "fond/api";
import { RECENT_PROJECTS_KEY } from "fond/constants";
import { useStarred } from "fond/projects/ItemMenu/hooks/useStarred";
import { SearchScope } from "fond/topBar/useSearchScope";
import { Folder, ProjectPreview, RecentlyViewed, Report, Store } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { getItem } from "fond/utils/localStorage";

/**
 * Given all folders and a root folder ID, returns a Set of all descendant folder IDs
 * (including the root itself) by building a parent->children map and doing a BFS.
 */
export function getDescendantFolderIds(allFolders: Folder[], rootFolderId: string): Set<string> {
  const childrenMap = new Map<string, string[]>();
  for (const folder of allFolders) {
    if (folder.ParentID) {
      const children = childrenMap.get(folder.ParentID);
      if (children) {
        children.push(folder.ID);
      } else {
        childrenMap.set(folder.ParentID, [folder.ID]);
      }
    }
  }

  const descendantIds = new Set<string>([rootFolderId]);
  const queue = [rootFolderId];
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const children = childrenMap.get(currentId);
    if (children) {
      for (const childId of children) {
        if (!descendantIds.has(childId)) {
          descendantIds.add(childId);
          queue.push(childId);
        }
      }
    }
  }

  return descendantIds;
}

interface FilterByScopeParams {
  scopeType: SearchScope["type"];
  isFolderScoped: boolean;
  descendantFolderIds: Set<string> | null;
  recentItemIds: Set<string>;
  starredItemIds: Set<string>;
  currentUsername: string | undefined;
}

function filterByScope<T extends { ID: string; Permission?: { Inherited?: unknown; IdentityType?: string } | null }>(
  items: T[],
  // A function that returns the folder ID for the given item (or null/undefined if not applicable)
  // FIXME: We should use consistent property names for folder references across entities (e.g., FolderID vs ParentID) to avoid needing this function.
  getFolderRef: (item: T) => string | null | undefined,
  // A function that returns the creator's username/email for the given item (or null/undefined if not applicable)
  // FIXME: We should use consistent property names for creator references across entities (e.g., Creator vs Owner) to avoid needing this function.
  getCreator: (item: T) => string | null | undefined,
  { scopeType, isFolderScoped, descendantFolderIds, recentItemIds, starredItemIds, currentUsername }: FilterByScopeParams
): T[] {
  if (isFolderScoped && descendantFolderIds) {
    return items.filter((item) => {
      const ref = getFolderRef(item);
      return ref != null && descendantFolderIds.has(ref);
    });
  }
  if (scopeType === "recent") return items.filter((item) => recentItemIds.has(item.ID));
  if (scopeType === "starred") return items.filter((item) => starredItemIds.has(item.ID));
  if (scopeType === "shared") {
    return items.filter((item) => !item.Permission?.Inherited && item.Permission?.IdentityType === "user" && getCreator(item) !== currentUsername);
  }
  return items;
}

interface ScopedSearchData {
  projects: ProjectPreview[];
  folders: Folder[];
  reports: Report[];
  /** The resolved folder name for folder/market_lens scopes */
  scopeFolderName: string | null;
  /** Whether scope data is still loading (e.g., folder name being fetched) */
  isScopeLoading: boolean;
}

/**
 * Hook that returns entity collections filtered by the given search scope.
 * Used by both GlobalSearch (autocomplete suggestions) and SearchResultContainer (full results page).
 */
export const useScopedSearchData = (scope: SearchScope): ScopedSearchData => {
  const allFolders = useSelector(selectAllFolders);
  const allProjects = useSelector(selectAllProjects);
  const allReports = useSelector(selectAllReports);
  const userId = useSelector(selectUserId);
  const currentAccount = useSelector(selectCurrentAccount);
  const currentUsername = useAppSelector((state: Store) => state.cognito.user?.username);

  // Resolve folder name from cache or via query when scope has a folderId
  const hasFolderId = scope.folderId != null;
  const cachedFolder = useAppSelector((state) => (hasFolderId ? selectFolderById(state, scope.folderId!) : undefined));
  const { data: fetchedFolder, isLoading: isFolderLoading } = useGetFolderQuery(scope.folderId ?? "", { skip: !hasFolderId });

  const scopeFolderName = scope.folderName ?? cachedFolder?.Name ?? fetchedFolder?.Name ?? null;
  const isScopeLoading = hasFolderId && isFolderLoading && !cachedFolder && !scope.folderName;

  const recentItemIds = useMemo(() => {
    const recentItems: RecentlyViewed[] = getItem(`${RECENT_PROJECTS_KEY}_${userId}_${currentAccount?.ID}`, []);
    return new Set(recentItems.map((item) => item.projectId));
  }, [userId, currentAccount?.ID]);

  const { starred } = useStarred();
  const starredItemIds = useMemo(() => new Set(starred), [starred]);

  const isFolderScoped = scope.type === "folder" || scope.type === "market_lens";

  const descendantFolderIds = useMemo(() => {
    if (isFolderScoped && scope.folderId) {
      return getDescendantFolderIds(allFolders, scope.folderId);
    }
    return null;
  }, [isFolderScoped, scope.folderId, allFolders]);

  const scopeParams: FilterByScopeParams = useMemo(
    () => ({ scopeType: scope.type, isFolderScoped, descendantFolderIds, recentItemIds, starredItemIds, currentUsername }),
    [scope.type, isFolderScoped, descendantFolderIds, recentItemIds, starredItemIds, currentUsername]
  );

  const projects = useMemo(
    () =>
      filterByScope(
        allProjects,
        (p) => p.FolderID,
        (p) => p.Creator,
        scopeParams
      ),
    [allProjects, scopeParams]
  );

  const folders = useMemo(
    () =>
      filterByScope(
        allFolders,
        (f) => f.ParentID,
        (f) => f.Creator,
        scopeParams
      ),
    [allFolders, scopeParams]
  );

  const reports = useMemo(
    () =>
      filterByScope(
        allReports,
        (r) => r.Folder?.ID,
        (r) => r.Creator?.Email,
        scopeParams
      ),
    [allReports, scopeParams]
  );

  return useMemo(
    () => ({ projects, folders, reports, scopeFolderName, isScopeLoading }),
    [projects, folders, reports, scopeFolderName, isScopeLoading]
  );
};
